import React from 'react';
import { styled } from '@mui/material/styles';
import { tUi } from '../../../../../../../../../utils/dq_lc';
import { HtmlContentColumn, HtmlContentWrapper, SlideTitle } from '../../../../../../styles/LearnTab/slideBase.styles';
import { CaseSectionStack, CaseSection, CaseSectionLabel } from './CaseStudyCard.styles';
import { LabMuted } from './HandsOnLabCard.styles';
import { inline, listOf, textOf } from './HandsOnLabCard.comp';

// Skill map (ThemeFirst): the whole requested skill as numbered parts, with this session's parts
// marked "today". The intro copy opens the session; the closing copy ("next") shows what follows.
// For young learners (audience "child") only the part names and their badges are shown.
const STATUS_COLOR = { today: '#0097B2', earlier: '#6B7280', later: '#9CA3AF' };

const MapList = styled('ol')`
  && {
    list-style: none;
    padding-left: 0;
    margin: 0;
  }

  li {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    padding: 8px 10px;
    border-radius: 8px;
  }

  li + li {
    margin-top: 6px;
  }

  li[data-map-status='today'] {
    background: #E0F4F7;
  }
`;

const MapBadge = styled('span')`
  flex: 0 0 auto;
  min-width: 64px;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1.5px solid currentColor;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
`;

export const ThemeMapCard = ({ slide, uiLocale = 'en-US' }) => {
  const origin = slide?._pbdq_origin || slide || {};
  const parts = listOf(origin.parts).filter((part) => textOf(part?.name));
  const status = listOf(origin.part_status);
  const nextMode = textOf(origin.mode) === 'next';
  const child = textOf(origin.audience) === 'child';

  return (
    <HtmlContentColumn data-theme-map={nextMode ? 'next' : 'today'} data-map-audience={child ? 'child' : 'all'}>
      {textOf(origin.headline) && <SlideTitle>{textOf(origin.headline)}</SlideTitle>}
      <HtmlContentWrapper>
        <CaseSectionStack>
          {textOf(origin.core_skill) && (
            <CaseSection data-map-section="coreSkill">
              <CaseSectionLabel>{tUi('slide.map.coreSkill', uiLocale)}</CaseSectionLabel>
              {inline(origin.core_skill)}
            </CaseSection>
          )}
          {parts.length > 0 && (
            <CaseSection data-map-section="parts">
              <CaseSectionLabel>{tUi(nextMode ? 'slide.map.partsNext' : 'slide.map.parts', uiLocale)}</CaseSectionLabel>
              <MapList>
                {parts.map((part, idx) => {
                  const state = ['today', 'earlier', 'later'].includes(status[idx]) ? status[idx] : 'later';
                  return (
                    <li key={idx} data-map-status={state}>
                      <MapBadge style={{ color: STATUS_COLOR[state] }}>{tUi(`slide.map.status.${state}`, uiLocale)}</MapBadge>
                      <div>
                        <strong>{idx + 1}. {inline(part.name)}</strong>
                        {!child && textOf(part.what_it_is) && <div>{inline(part.what_it_is)}</div>}
                        {!child && textOf(part.can_do) && <LabMuted>{inline(part.can_do)}</LabMuted>}
                      </div>
                    </li>
                  );
                })}
              </MapList>
            </CaseSection>
          )}
          {textOf(origin.scope_note) && (
            <CaseSection data-map-section="scope">
              <CaseSectionLabel>{tUi('slide.map.scope', uiLocale)}</CaseSectionLabel>
              {inline(origin.scope_note)}
            </CaseSection>
          )}
        </CaseSectionStack>
      </HtmlContentWrapper>
    </HtmlContentColumn>
  );
};

export default ThemeMapCard;
